import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cards from '@cloudscape-design/components/cards';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Tabs from '@cloudscape-design/components/tabs';
import TextFilter from '@cloudscape-design/components/text-filter';
import Icon from '@cloudscape-design/components/icon';

// API and hooks
import { programsApi, ProgramForUser, CohortSummary } from '../api';
import { useAuthStore } from '../stores/authStore';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorAlert } from '../components/ErrorAlert';
import { CategoryBadge } from '../components/CategoryBadge';
import { UserRoleBadge } from '../components/UserRoleBadge';
import { HelpPopover } from '../components/HelpPopover';
import { getUserProgramRelationship } from '../utils';
import { canCreateProgram } from '../utils/permissions';

interface ProgramWithStatus extends ProgramForUser {}

function TrainingPrograms() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const [activeTabId, setActiveTabId] = useState<string>('all');
  const [programs, setPrograms] = useState<ProgramForUser[]>([]);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Fetch programs for user (includes cohort summaries and user relationships)
  useEffect(() => {
    if (!currentUser?.userId) return;
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch programs with cohort summaries and user relationships in one call
        const programsData = await programsApi.listForUser(currentUser?.userId ?? '');
        setPrograms(programsData);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [currentUser?.userId]);

  if (!currentUser) return null;

  // Format date for badges (e.g., "3/15" or "12/31")
  // Parse as local date to avoid timezone shifts
  const formatBadgeDate = (dateStr: string): string => {
    // Parse YYYY-MM-DD as local date (not UTC)
    const [, month, day] = dateStr.split('-').map(Number);
    return `${month}/${day}`;
  };

  // Format ended date with 2-digit year (e.g., "3/15/24") so past cohorts spanning years are unambiguous
  const formatEndedDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${month}/${day}/${String(year).slice(2)}`;
  };

  // Parse date string as local date for consistent sorting
  const parseLocalDate = (dateStr: string): number => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).getTime();
  };

  // Get the relevant cohort for a program based on current tab
  const getRelevantCohort = (program: ProgramForUser, tab: string): CohortSummary | null => {
    if (tab === 'all') {
      return program.upcomingCohort || program.activeCohort || program.completedCohort || null;
    }

    if (tab === 'upcoming') {
      return program.upcomingCohort || null;
    }
    
    if (tab === 'in-progress') {
      return program.activeCohort || null;
    }
    
    if (tab === 'completed') {
      return program.completedCohort || null;
    }
    
    return null;
  };

  // Get user's relationship to a specific cohort (context-aware for the current tab)
  const getUserRelationship = (cohort: CohortSummary | null, isAdmin: boolean, isOwner: boolean = false) => {
    return getUserProgramRelationship(cohort, isAdmin, isOwner);
  };

  // Sort programs by status priority and then alphabetically
  const sortPrograms = (programs: ProgramWithStatus[], tab: 'upcoming' | 'in-progress' | 'completed'): ProgramWithStatus[] => {
    return [...programs].sort((a, b) => {
      const aRelevantCohort = getRelevantCohort(a, tab);
      const bRelevantCohort = getRelevantCohort(b, tab);

      // Upcoming tab: Sort by registration date (or start date if no registration)
      if (tab === 'upcoming' && aRelevantCohort && bRelevantCohort) {
        // Use registration end date if available, otherwise start date
        const aDate = aRelevantCohort.registrationEndDate 
          ? parseLocalDate(aRelevantCohort.registrationEndDate)
          : parseLocalDate(aRelevantCohort.startDate);
        const bDate = bRelevantCohort.registrationEndDate 
          ? parseLocalDate(bRelevantCohort.registrationEndDate)
          : parseLocalDate(bRelevantCohort.startDate);
        
        if (aDate !== bDate) {
          return aDate - bDate;
        }
      }

      // In Progress tab: Sort by end date (ending soonest first)
      if (tab === 'in-progress' && aRelevantCohort && bRelevantCohort) {
        const aEndDate = parseLocalDate(aRelevantCohort.endDate);
        const bEndDate = parseLocalDate(bRelevantCohort.endDate);
        if (aEndDate !== bEndDate) {
          return aEndDate - bEndDate;
        }
      }

      // Inactive/Completed tab: Sort by end date (most recently completed first)
      if (tab === 'completed' && aRelevantCohort && bRelevantCohort) {
        const aEndDate = parseLocalDate(aRelevantCohort.endDate);
        const bEndDate = parseLocalDate(bRelevantCohort.endDate);
        if (aEndDate !== bEndDate) {
          return bEndDate - aEndDate; // Reverse order for most recent first
        }
      }

      // All tabs: Secondary sort by program name (alphabetical)
      return a.programName.localeCompare(b.programName);
    });
  };

  // Categorize programs - programs can appear in multiple tabs
  const programsWithStatus: ProgramWithStatus[] = programs.map(program => ({
    ...program,
  }));

  // Apply text filter across all tabs
  const lowerFilter = filterText.toLowerCase();
  const filteredPrograms = lowerFilter
    ? programsWithStatus.filter(p => p.programName.toLowerCase().includes(lowerFilter))
    : programsWithStatus;

  // Categorize programs - programs can appear in multiple tabs based on their cohorts
  const upcomingPrograms = sortPrograms(
    filteredPrograms.filter(p => p.upcomingCohort && p.isEligible),
    'upcoming'
  );
  const inProgressPrograms = sortPrograms(
    filteredPrograms.filter(p => p.activeCohort && p.isEligible),
    'in-progress'
  );
  const pastPrograms = sortPrograms(
    filteredPrograms.filter(p => p.completedCohort && !p.activeCohort && !p.upcomingCohort && p.isEligible),
    'completed'
  );
  const allPrograms = [...filteredPrograms].sort((a, b) => a.programName.localeCompare(b.programName));

  // Show loading state
  if (loading) {
    return <LoadingSpinner message="Loading programs..." />;
  }

  // Show error state
  if (error) {
    return <ErrorAlert error={error} onRetry={() => window.location.reload()} />;
  }

  // Render program cards with tab-specific badge logic
  const renderProgramCards = (programs: ProgramWithStatus[], currentTab: string) => {
    const cardDefinition = {
      header: (item: ProgramWithStatus) => {
        return (
          <SpaceBetween size="xs">
            <Link 
              fontSize="heading-m" 
              href="#"
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/programs/${item.programId}`);
              }}
            >
              {item.programName}
            </Link>
          </SpaceBetween>
        );
      },
      sections: [
        {
          id: 'content',
          content: (item: ProgramWithStatus) => {
            const relevantCohort = getRelevantCohort(item, currentTab);
            const hasUpcomingCohorts = !!item.upcomingCohort;
            const isProgramEligible = item.isEligible;

            return (
              <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100px', paddingTop: '8px' }}>
                <div style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'var(--color-text-body-secondary)',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  marginBottom: '6px',
                }}>
                  {item.description}
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                  <SpaceBetween size="s">
                    {/* Row 1: Category and cohort date badges */}
                    <SpaceBetween direction="horizontal" size="xs">
                      {/* Program Type (dark gray) */}
                      <CategoryBadge category={item.category} />
                      
                      {/* Upcoming tab badges - registration date first, then start date */}
                      {currentTab === 'upcoming' && relevantCohort && relevantCohort.status === 'registration-open' && relevantCohort.registrationEndDate && (
                        <Badge color="blue">Registration ends {formatBadgeDate(relevantCohort.registrationEndDate)}</Badge>
                      )}
                      
                      {currentTab === 'upcoming' && relevantCohort && relevantCohort.status === 'upcoming' && relevantCohort.registrationStartDate && 
                       new Date(relevantCohort.registrationStartDate) > new Date() && (
                        <Badge color="blue">Registration opens {formatBadgeDate(relevantCohort.registrationStartDate)}</Badge>
                      )}
                      
                      {currentTab === 'upcoming' && relevantCohort && (
                        <Badge color="green">Starts {formatBadgeDate(relevantCohort.startDate)}</Badge>
                      )}

                      {/* In-progress tab badges */}
                      {currentTab === 'in-progress' && relevantCohort && (
                        <Badge color="green">Ends {formatBadgeDate(relevantCohort.endDate)}</Badge>
                      )}
                      
                      {currentTab === 'in-progress' && relevantCohort && relevantCohort.status === 'active' && relevantCohort.registrationEndDate && 
                       new Date(relevantCohort.registrationEndDate) > new Date() && (
                        <Badge color="blue">Registration ends {formatBadgeDate(relevantCohort.registrationEndDate)}</Badge>
                      )}
                      
                      {currentTab === 'in-progress' && relevantCohort && relevantCohort.status === 'active' && (!relevantCohort.registrationEndDate || 
                       new Date(relevantCohort.registrationEndDate) <= new Date()) && (
                        <Badge>Registration closed</Badge>
                      )}

                      {/* Inactive tab badges */}
                      {currentTab === 'completed' && relevantCohort && (
                        <Badge>Ended {formatEndedDate(relevantCohort.endDate)}</Badge>
                      )}
                      
                      {currentTab === 'completed' && !hasUpcomingCohorts && (
                        <Badge>No upcoming cohorts</Badge>
                      )}

                      {/* All tab — category and unpublished only */}

                      {/* {currentTab === 'all' && relevantCohort && relevantCohort.status === 'registration-open' && relevantCohort.registrationEndDate && (
                        <Badge color="blue">Registration ends {formatBadgeDate(relevantCohort.registrationEndDate)}</Badge>
                      )}
                      {currentTab === 'all' && relevantCohort && (relevantCohort.status === 'upcoming' || relevantCohort.status === 'registration-open') && (
                        <Badge color="green">Starts {formatBadgeDate(relevantCohort.startDate)}</Badge>
                      )}
                      {currentTab === 'all' && relevantCohort && relevantCohort.status === 'active' && (
                        <Badge color="green">Ends {formatBadgeDate(relevantCohort.endDate)}</Badge>
                      )}
                      {currentTab === 'all' && relevantCohort && relevantCohort.status === 'active' && relevantCohort.registrationEndDate &&
                       new Date(relevantCohort.registrationEndDate) > new Date() && (
                        <Badge color="blue">Registration ends {formatBadgeDate(relevantCohort.registrationEndDate)}</Badge>
                      )}
                      {currentTab === 'all' && relevantCohort && relevantCohort.status === 'active' && (!relevantCohort.registrationEndDate ||
                       new Date(relevantCohort.registrationEndDate) <= new Date()) && (
                        <Badge>Registration closed</Badge>
                      )}
                      {currentTab === 'all' && relevantCohort && relevantCohort.status === 'completed' && (
                        <Badge>Ended {formatEndedDate(relevantCohort.endDate)}</Badge>
                      )}
                      {currentTab === 'all' && !relevantCohort && (
                        <Badge>No cohorts</Badge>
                      )} */}
                      
                      {/* TODO: Uncomment when isPublished flow is fully tested
                      {!item.isPublished && (
                        <Badge color="red">Unpublished</Badge>
                      )}
                      */}
                    </SpaceBetween>
                  </SpaceBetween>
                </div>
                {/* Org restriction + user role badge at bottom of card */}
                {item.visibility === 'org-restricted' && item.orgLeader && (
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon
                      name={isProgramEligible ? 'group-active' : 'group'}
                      variant={isProgramEligible ? 'subtle' : 'warning'}
                      size="small"
                    />
                    <span style={{
                      fontSize: 'var(--font-size-body-s)',
                      color: isProgramEligible ? '#656871' : '#855900',
                    }}>
                      Restricted to <strong>{item.orgLeaderName ?? `@${item.orgLeader}`}</strong>'s org
                    </span>
                  </div>
                )}
                {getUserRelationship(relevantCohort, item.isAdmin, item.isOwner) && (
                  <div style={{ marginTop: item.visibility === 'org-restricted' && item.orgLeader ? '6px' : '12px' }}>
                    <UserRoleBadge role={getUserRelationship(relevantCohort, item.isAdmin, item.isOwner)!.role as 'owner' | 'admin' | 'mentor' | 'participant'} context="program" />
                  </div>
                )}
              </div>
            );
          },
        },
      ],
    };

    return (
      <Cards
        cardDefinition={cardDefinition}
        cardsPerRow={cardsPerRow}
        items={programs}
        loadingText="Loading programs"
        trackBy="programId"
        empty={
          <Box textAlign="center" color="inherit">
            <Box variant="p" color="inherit">No programs available</Box>
          </Box>
        }
      />
    );
  };

  const cardsPerRow = [
    { cards: 1 },
    { minWidth: 500, cards: 2 },
    { minWidth: 1000, cards: 3 }
  ];

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        info={
          <HelpPopover
            header="Training Programs"
            content="Training programs group cohorts of participants around a shared learning goal. Each program can have multiple cohorts running at different times. Any manager at AWS can create a new training program."
          />
        }
        actions={
          canCreateProgram(currentUser) ? (
            <Button 
              iconName="add-plus" 
              variant="normal"
              onClick={() => navigate('/programs/create')}
            >
              Create Program
            </Button>
          ) : undefined
        }
      >
        Training Programs
      </Header>

      <TextFilter
        filteringText={filterText}
        onChange={({ detail }) => setFilterText(detail.filteringText)}
        filteringPlaceholder="Find programs..."
        filteringAriaLabel="Filter programs"
        countText={filterText ? `${allPrograms.length} ${allPrograms.length === 1 ? 'match' : 'matches'}` : undefined}
      />

      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
        tabs={[
          {
            id: 'all',
            label: `All Programs (${allPrograms.length})`,
            content: renderProgramCards(allPrograms, 'all'),
          },
          {
            id: 'upcoming',
            label: `Upcoming (${upcomingPrograms.length})`,
            content: renderProgramCards(upcomingPrograms, 'upcoming'),
          },
          {
            id: 'in-progress',
            label: `In Progress (${inProgressPrograms.length})`,
            content: renderProgramCards(inProgressPrograms, 'in-progress'),
          },
          {
            id: 'completed',
            label: `Completed (${pastPrograms.length})`,
            content: renderProgramCards(pastPrograms, 'completed'),
          },
        ]}
      />
    </SpaceBetween>
  );
}

export default TrainingPrograms;
